import { useCallback, useEffect, useMemo, useRef } from 'react';
import { type KeyedMutator } from 'swr';

import { getBuildShaIfInitialized } from '@apps/web/lib/runtime_config';

/**
 * Shares one refetch of an SWR key across every browser frame running this
 * build, so N open frames cost one request per window instead of N.
 *
 * The Chrome extension sidebar is the motivating case. Each EHR tab hosts its
 * own sidebar frame, every frame listens to the same organization-wide event
 * stream, and each one refetched the home feed on its own throttle clock. A
 * coordinator with several EHR tabs open therefore made several identical
 * reads per window and was rejected by the API's per-user, per-route limit.
 *
 * Coordination has three parts:
 *
 * - A Web Lock arbitrates the claim. Only one frame at a time may decide
 *   whether the window is free, which is what makes "one request per window"
 *   a guarantee rather than a probability.
 * - `localStorage` is the shared clock, with an in-memory copy behind it so
 *   the window still holds when storage is unavailable.
 * - `BroadcastChannel` distributes the result, tagged with the window it was
 *   fetched for so a slow read from an earlier window cannot land on top of a
 *   newer one. Frames that lost the claim
 *   receive the winner's payload and write it into their own SWR cache, so
 *   skipping the fetch costs them no freshness. Without it the window stays
 *   local to each frame, rather than a frame skipping a read it never gets.
 *
 * All three are partitioned by top-level site, so frames embedded in different
 * host sites coordinate within their own group rather than globally. That
 * still collapses the case this exists for: many tabs of one EHR.
 *
 * The channel, lock, and storage keys carry the build SHA. Frames running
 * different releases never exchange payloads, so a response shape that changed
 * between releases cannot reach the other build's cache.
 */

const BROADCAST_MESSAGE_TYPE = 'cross-frame-shared-refresh';

type SharedRefreshMessage = {
    readonly type: typeof BROADCAST_MESSAGE_TYPE;
    readonly scopeKey: string;
    /** The window this payload was fetched for; orders competing payloads. */
    readonly claimedAtMs: number;
    readonly data: unknown;
};

function isSharedRefreshMessage(value: unknown): value is SharedRefreshMessage {
    return (
        typeof value === 'object' &&
        value !== null &&
        (value as SharedRefreshMessage).type === BROADCAST_MESSAGE_TYPE &&
        typeof (value as SharedRefreshMessage).scopeKey === 'string' &&
        Number.isFinite((value as SharedRefreshMessage).claimedAtMs)
    );
}

// The newest window whose payload this frame has already taken, so a slow
// fetch from an earlier window cannot land on top of a newer one. A window
// that overruns its own length leaves the next one free to start, so two
// fetches can be in flight and finish out of order.
const lastAppliedClaimAtMsByKey = new Map<string, number>();

// Backs the shared clock: it holds this frame's own window when storage is
// unavailable, and it is the whole clock when there is no channel to share
// results through.
const lastClaimAtMsByKey = new Map<string, number>();

function buildKeyPrefix(name: string): string {
    // The build is always known in the running app, which boots on the loaded
    // config; the fallback only covers frames that render without one.
    return `${name}:${getBuildShaIfInitialized() ?? 'unknown-build'}`;
}

function buildClaimKey(keyPrefix: string, scopeKey: string): string {
    return `phoebe-shared-refresh:${keyPrefix}:${scopeKey}`;
}

function readLastClaimAtMs(
    claimKey: string,
    isCrossFrame: boolean,
): number | null {
    const inMemoryClaimAtMs = lastClaimAtMsByKey.get(claimKey) ?? null;
    if (!isCrossFrame) {
        return inMemoryClaimAtMs;
    }
    try {
        const rawClaimAtMs = window.localStorage.getItem(claimKey);
        const storedClaimAtMs =
            rawClaimAtMs === null ? null : Number(rawClaimAtMs);
        if (storedClaimAtMs !== null && Number.isFinite(storedClaimAtMs)) {
            return Math.max(storedClaimAtMs, inMemoryClaimAtMs ?? 0);
        }
    } catch {
        // Storage is unavailable; the in-memory clock still bounds this frame.
    }
    return inMemoryClaimAtMs;
}

function recordClaim(
    claimKey: string,
    claimAtMs: number,
    isCrossFrame: boolean,
): void {
    lastClaimAtMsByKey.set(claimKey, claimAtMs);
    if (!isCrossFrame) {
        return;
    }
    try {
        window.localStorage.setItem(claimKey, String(claimAtMs));
    } catch {
        // See readLastClaimAtMs: this frame falls back to its own clock, and
        // other frames keep theirs, so a storage-less browser degrades to
        // per-frame throttling rather than losing the window entirely.
    }
}

/**
 * Give the window back after a failed fetch, unless another frame has already
 * taken it: releasing a claim that is no longer ours would hand a second frame
 * the same window we were trying to free.
 */
function releaseClaim(
    claimKey: string,
    claimedAtMs: number,
    isCrossFrame: boolean,
): void {
    if (readLastClaimAtMs(claimKey, isCrossFrame) !== claimedAtMs) {
        return;
    }
    lastClaimAtMsByKey.delete(claimKey);
    if (!isCrossFrame) {
        return;
    }
    try {
        window.localStorage.removeItem(claimKey);
    } catch {
        // Nothing to release when storage is unavailable.
    }
}

/**
 * Accept a payload unless it is older than the last one taken for this scope,
 * and remember it. The invariant is that a cache never moves backwards, so
 * re-taking the same window is allowed and only an earlier one is refused.
 */
function takeNewerPayload(claimKey: string, claimedAtMs: number): boolean {
    const lastAppliedClaimAtMs = lastAppliedClaimAtMsByKey.get(claimKey);
    if (
        lastAppliedClaimAtMs !== undefined &&
        claimedAtMs < lastAppliedClaimAtMs
    ) {
        return false;
    }
    lastAppliedClaimAtMsByKey.set(claimKey, claimedAtMs);
    return true;
}

/**
 * Take the scope's fetch slot for this window, or report that another frame
 * already holds it.
 *
 * Reading the clock and stamping it must not interleave with another frame
 * doing the same, or two frames both see a free window and both fetch. A Web
 * Lock makes that decision exclusive across frames; `ifAvailable` means a
 * frame that finds the lock held declines immediately, because whoever holds
 * it is about to take the window anyway.
 *
 * A browser without Web Locks keeps the plain read-then-stamp, where frames
 * that interleave exactly can both take one window. The platform offers no
 * atomic compare-and-set on storage to close that, and the cost is a second
 * read of the same window, never a wrong or stale one: payloads are ordered
 * by window, so whichever lands last is the one every frame keeps. Every
 * browser the extension runs in has Web Locks.
 */
async function claimSharedRefresh(
    claimKey: string,
    throttleMs: number,
    isCrossFrame: boolean,
): Promise<number | null> {
    const decideUnderLock = (): number | null => {
        const nowMs = Date.now();
        const lastClaimAtMs = readLastClaimAtMs(claimKey, isCrossFrame);
        if (lastClaimAtMs !== null && nowMs - lastClaimAtMs < throttleMs) {
            return null;
        }
        recordClaim(claimKey, nowMs, isCrossFrame);
        return nowMs;
    };

    const lockManager =
        typeof navigator === 'undefined' ? undefined : navigator.locks;
    if (lockManager === undefined || !isCrossFrame) {
        return decideUnderLock();
    }

    return await lockManager.request(claimKey, { ifAvailable: true }, (lock) =>
        lock === null ? null : decideUnderLock(),
    );
}

export function useCrossFrameSharedRefresh<TData>({
    isData,
    mutate,
    name,
    refresh,
    scopeKey,
    throttleMs,
}: Readonly<{
    /**
     * Validates a payload from another frame before it reaches the SWR cache.
     * Required so a channel this hook does not own, or a malformed producer,
     * cannot write an unchecked value into a typed cache.
     */
    readonly isData: (value: unknown) => value is TData;
    /** Applies a payload received from another frame to this frame's cache. */
    readonly mutate: KeyedMutator<TData>;
    /** Identifies the shared data, e.g. `extension-sidebar-home-feed`. */
    readonly name: string;
    /** Refetches the data and rejects when that request fails. */
    readonly refresh: () => Promise<TData | undefined>;
    /** Everything the SWR key varies on, e.g. organization and mode. */
    readonly scopeKey: string | null;
    readonly throttleMs: number;
}>): () => Promise<void> {
    const keyPrefix = useMemo(() => buildKeyPrefix(name), [name]);
    // One channel object per frame, used to both listen and publish: a
    // channel never delivers its own posts, so this is what keeps the frame
    // that fetched from re-applying its own payload.
    const channelRef = useRef<BroadcastChannel | null>(null);
    const isDataRef = useRef(isData);
    isDataRef.current = isData;

    useEffect(() => {
        if (scopeKey === null || typeof BroadcastChannel === 'undefined') {
            return;
        }

        const channel = new BroadcastChannel(keyPrefix);
        channelRef.current = channel;
        channel.onmessage = (event: MessageEvent<unknown>) => {
            if (
                !isSharedRefreshMessage(event.data) ||
                event.data.scopeKey !== scopeKey ||
                !isDataRef.current(event.data.data) ||
                !takeNewerPayload(
                    buildClaimKey(keyPrefix, scopeKey),
                    event.data.claimedAtMs,
                )
            ) {
                return;
            }
            // Writing the peer's payload without revalidating is what makes
            // losing the claim free.
            void mutate(event.data.data, { revalidate: false });
        };

        return () => {
            channelRef.current = null;
            channel.close();
        };
    }, [keyPrefix, mutate, scopeKey]);

    return useCallback(async () => {
        if (scopeKey === null) {
            return;
        }
        // Suppressing another frame's fetch is only safe when its result can
        // be handed over, so without a channel the window stays local to this
        // frame: callers on one key still share a read, other frames do not.
        const isCrossFrame = channelRef.current !== null;
        const claimKey = buildClaimKey(keyPrefix, scopeKey);
        const claimedAtMs = await claimSharedRefresh(
            claimKey,
            throttleMs,
            isCrossFrame,
        );
        if (claimedAtMs === null) {
            return;
        }

        let data: TData | undefined;
        try {
            data = await refresh();
        } catch {
            // A frame that cannot fetch must not hold the scope's slot, or it
            // starves the frames that can.
            releaseClaim(claimKey, claimedAtMs, isCrossFrame);
            return;
        }

        if (data === undefined) {
            // No payload means peers cannot receive the refresh they skipped.
            releaseClaim(claimKey, claimedAtMs, isCrossFrame);
            return;
        }

        // This frame's own cache already holds this payload, so record the
        // window here too: a slower peer from an earlier one must not
        // overwrite it.
        takeNewerPayload(claimKey, claimedAtMs);
        channelRef.current?.postMessage({
            type: BROADCAST_MESSAGE_TYPE,
            scopeKey,
            claimedAtMs,
            data,
        } satisfies SharedRefreshMessage);
    }, [keyPrefix, refresh, scopeKey, throttleMs]);
}
